/**
 * SearchFiltersPanelContent — Koordinator search + filters panel content.
 * Rendered inside the shared DesktopRightPanel / RightDrawerShell in "search" mode.
 *
 * Uses real taxonomy filters (industry → category drill-down) from the discovery system.
 * Filter state is lifted to DashboardLayout — this component is controlled.
 */
import React, { useState, useCallback, useMemo } from 'react';
import { Search, X, SlidersHorizontal, Building2, Tag, Loader2 } from 'lucide-react';
import { cn } from '@bright-web-skeleton/ui';

export interface TaxonomyIndustry {
  id: string;
  label: string;
  slug?: string;
}

export interface TaxonomyCategory {
  id: string;
  label: string;
  slug?: string;
  industry_id: string;
}

export interface SearchFiltersPanelContentProps {
  /** Current committed search query */
  searchQuery: string;
  /** Called when user submits a search */
  onSearchCommit: (query: string) => void;

  /** Full taxonomy lists (loaded by parent) */
  industries: TaxonomyIndustry[];
  categories: TaxonomyCategory[];
  taxonomyLoading?: boolean;

  /** Lifted filter state */
  selectedIndustryIds: string[];
  selectedCategoryIds: string[];
  onToggleIndustry: (id: string) => void;
  onToggleCategory: (id: string) => void;
  onClearAll: () => void;
}

export const SearchFiltersPanelContent: React.FC<SearchFiltersPanelContentProps> = ({
  searchQuery: externalQuery,
  onSearchCommit,
  industries,
  categories,
  taxonomyLoading = false,
  selectedIndustryIds,
  selectedCategoryIds,
  onToggleIndustry,
  onToggleCategory,
  onClearAll,
}) => {
  const [localQuery, setLocalQuery] = useState(externalQuery);

  // Sync external query changes
  React.useEffect(() => {
    setLocalQuery(externalQuery);
  }, [externalQuery]);

  const handleSubmit = useCallback((e?: React.FormEvent) => {
    e?.preventDefault();
    onSearchCommit(localQuery);
  }, [localQuery, onSearchCommit]);

  const handleClearAll = useCallback(() => {
    setLocalQuery('');
    onClearAll();
  }, [onClearAll]);

  // Derive available categories based on selected industries
  const availableCategories = useMemo(() => {
    if (selectedIndustryIds.length === 0) return [];
    return categories.filter(c => selectedIndustryIds.includes(c.industry_id));
  }, [categories, selectedIndustryIds]);

  const hasFilters = selectedIndustryIds.length > 0 || selectedCategoryIds.length > 0 || localQuery.length > 0;

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {/* Search input */}
        <form onSubmit={handleSubmit} className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <input
            type="text"
            value={localQuery}
            onChange={e => setLocalQuery(e.target.value)}
            placeholder="Search creators, proposals, orders…"
            className="w-full rounded-xl border border-border bg-background pl-10 pr-10 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
            style={{ fontSize: '16px' }}
          />
          {localQuery && (
            <button
              type="button"
              onClick={() => { setLocalQuery(''); onSearchCommit(''); }}
              className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 flex items-center justify-center rounded-full hover:bg-muted text-muted-foreground"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
          {/* Hidden bridge button for mobile footer CTA */}
          <button
            type="submit"
            data-panel-search="true"
            className="sr-only"
            tabIndex={-1}
            aria-hidden="true"
          >
            Search
          </button>
        </form>

        {/* Filters section */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
              <SlidersHorizontal className="h-4 w-4" />
              Filters
            </h3>
            {hasFilters && (
              <button onClick={handleClearAll} className="text-xs text-primary hover:underline">
                Clear all
              </button>
            )}
          </div>

          {taxonomyLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading filters…
            </div>
          ) : (
            <>
              {/* Industry filter */}
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wider flex items-center gap-1.5">
                  <Building2 className="w-3.5 h-3.5" />
                  Industry
                </p>
                {industries.length === 0 ? (
                  <p className="text-xs text-muted-foreground italic">No industries available</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {industries.map(ind => (
                      <button
                        key={ind.id}
                        onClick={() => onToggleIndustry(ind.id)}
                        className={cn(
                          'px-3 py-1.5 rounded-full text-xs font-medium border transition-colors',
                          selectedIndustryIds.includes(ind.id)
                            ? 'bg-primary text-primary-foreground border-primary'
                            : 'bg-background text-foreground border-border hover:bg-muted',
                        )}
                      >
                        {ind.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Category filter — shown when at least one industry is selected */}
              {selectedIndustryIds.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wider flex items-center gap-1.5">
                    <Tag className="w-3.5 h-3.5" />
                    Category
                  </p>
                  {availableCategories.length === 0 ? (
                    <p className="text-xs text-muted-foreground italic">No categories for selected industries</p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {availableCategories.map(cat => (
                        <button
                          key={cat.id}
                          onClick={() => onToggleCategory(cat.id)}
                          className={cn(
                            'px-3 py-1.5 rounded-full text-xs font-medium border transition-colors',
                            selectedCategoryIds.includes(cat.id)
                              ? 'bg-primary text-primary-foreground border-primary'
                              : 'bg-background text-foreground border-border hover:bg-muted',
                          )}
                        >
                          {cat.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {/* Active filter summary */}
          {hasFilters && !taxonomyLoading && (
            <div className="pt-2 border-t border-border">
              <p className="text-xs text-muted-foreground mb-2">Active filters:</p>
              <div className="flex flex-wrap gap-1.5">
                {localQuery && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-primary/10 text-primary text-xs">
                    "{localQuery}"
                    <button onClick={() => { setLocalQuery(''); onSearchCommit(''); }} className="hover:text-primary/70">
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                )}
                {selectedIndustryIds.map(id => {
                  const ind = industries.find(i => i.id === id);
                  return ind ? (
                    <span key={id} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-primary/10 text-primary text-xs">
                      {ind.label}
                      <button onClick={() => onToggleIndustry(id)} className="hover:text-primary/70">
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ) : null;
                })}
                {selectedCategoryIds.map(id => {
                  const cat = categories.find(c => c.id === id);
                  return cat ? (
                    <span key={id} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-primary/10 text-primary text-xs">
                      {cat.label}
                      <button onClick={() => onToggleCategory(id)} className="hover:text-primary/70">
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ) : null;
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
